import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Flex, Grid, Row, TimePicker, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import BrandButton from "../../../components/v2/Buttons/BrandButton";
import {
  ACTIONTYPES,
  useStore,
  useStoreDispatch,
} from "../../../context/CreateStoreContext";
import { checkSchedule } from "../../../helpers/storeHoursValidator";
import useComponentSize from "../../../hooks/useComponentSize";
import style from "./StoreHours.module.css";
import TimePickerModal from "./TimePickerModal";

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

export default function StoreHoursTest({ proceed, back }) {
  const componentSize = useComponentSize();
  const dispatch = useStoreDispatch();
  const { operationHours = [] } = useStore();
  const [hours, setHours] = useState(operationHours);
  const [updating, setUpdating] = useState(true);
  const [openTimePickerModal, setOpenTimePickerModal] = useState({
    open: false,
    schedIndex: null,
  });

  const validateAllSchedules = (validateAll = false) => {
    const validatedSched = hours.map((sched, index) => {
      // validate only if has error to avoid tagging errors to valid/validated schedule(s)
      if (
        sched?.error?.status ||
        sched?.sched?.error?.message ||
        sched?.error?.invalidTime ||
        sched?.error?.invalidDays ||
        validateAll
      ) {
        const error = checkSchedule(sched, index, hours);
        sched.error = error;
      }
      return sched;
    });

    setHours(validatedSched);

    return validatedSched.some(
      ({ error = {} }) =>
        error?.status ||
        error?.message ||
        error?.invalidTime ||
        error?.invalidDays,
    );
  };

  const handleSubmit = () => {
    if (validateAllSchedules(true)) return;

    // clean up id

    const cleaned = hours.map((sched, index) => ({
      ...sched,
      id: String(index + 1),
    }));

    dispatch({ type: ACTIONTYPES.OPERATIONHOURS, data: cleaned });
    proceed();
  };

  const handleAddSchedule = () => {
    setHours((prev) => [
      ...prev,
      {
        id: String(prev.length + 1),
        days: [],
        closeTime: "",
        openTime: "",
        type: "DAILY_HOURS",
      },
    ]);
  };

  const handleRemoveSchedule = (index) => {
    if (hours.length === 1) {
      return;
    }
    setHours((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDayClick = (schedIndex, dayClicked) => {
    setUpdating(true);
    setHours((prev) => {
      const scheds = [...prev];

      const days = new Set(scheds[schedIndex].days || []);

      if (days.has(dayClicked)) {
        days.delete(dayClicked);
      } else {
        days.add(dayClicked);
      }

      const updatedSched = {
        ...scheds[schedIndex],
        days: [...days].sort((a, b) => a - b),
      };

      const error = checkSchedule(updatedSched, schedIndex, hours);
      updatedSched.error = error;
      scheds[schedIndex] = updatedSched;

      return scheds;
    });
    setUpdating(false);
  };

  const handleTimeChange = (schedIndex, timeString = []) => {
    setUpdating(true);
    setHours((prev) => {
      const scheds = [...prev];

      const updatedSched = {
        ...scheds[schedIndex],
        closeTime: timeString?.[1] || "",
        openTime: timeString?.[0] || "",
      };

      const error = checkSchedule(updatedSched, schedIndex, hours);
      updatedSched.error = error;
      scheds[schedIndex] = updatedSched;

      return scheds;
    });

    setUpdating(false);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      !updating && validateAllSchedules();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [updating]);

  return (
    <>
      <Row className="BevzForm">
        <Col
          xs={{ span: 22, offset: 1 }}
          lg={{ span: 20, offset: 2 }}
          xl={{ span: 15, offset: 2 }}
        >
          <Title
            level={componentSize === "default" ? 4 : 2}
            className="BevzFormHeader"
          >
            Store Hours
          </Title>
          <Paragraph className="BevzFormSubHeader">
            Click the days and set the time of your store operation hours. You
            should have at least one schedule filled.
          </Paragraph>

          <Row style={{ paddingTop: "1.375rem" }}>
            <Col span={24}>
              {hours.map((hour, index) => (
                <Schedules
                  key={index}
                  {...hour}
                  schedId={hour?.id}
                  schedIndex={index}
                  onRemove={handleRemoveSchedule}
                  onDayClick={handleDayClick}
                  onTimeChange={handleTimeChange}
                  showDeleteIcon={hours.length > 1}
                  componentSize={componentSize}
                  setOpenTimePickerModal={setOpenTimePickerModal}
                />
              ))}
            </Col>
            <Col span={24} style={{ paddingTop: "50px" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddSchedule}
                disabled={hours.length === 20}
              >
                Add Schedule
              </Button>
            </Col>
          </Row>

          <Row
            align="middle"
            justify="space-between"
            className="BevzFormButtonSection"
          >
            <Col>
              <Button
                size="large"
                type="default"
                shape="round"
                icon={<ArrowLeftOutlined />}
                onClick={back}
              >
                Back
              </Button>
            </Col>
            <Col>
              <BrandButton
                type="solidyellow"
                htmlType="submit"
                onSubmit={handleSubmit}
                size="large"
              >
                {componentSize === "default"
                  ? "Proceed"
                  : "Proceed to Subscription Plan"}
              </BrandButton>
            </Col>
          </Row>
        </Col>
      </Row>
      <TimePickerModal
        open={openTimePickerModal.open}
        schedIndex={openTimePickerModal.schedIndex}
        startTime={openTimePickerModal.startTime}
        endTime={openTimePickerModal.endTime}
        handleOk={(schedIndex, timeString) => {
          handleTimeChange(schedIndex, timeString);
          setOpenTimePickerModal({ open: false, schedIndex: null });
        }}
        handleCancel={() =>
          setOpenTimePickerModal({ open: false, schedIndex: null })
        }
      />
    </>
  );
}

const Schedules = ({
  schedIndex,
  schedId,
  days,
  onRemove,
  onDayClick,
  onTimeChange,
  openTime,
  closeTime,
  error,
  showDeleteIcon = false,
  componentSize = "default",
  setOpenTimePickerModal,
}) => {
  const screens = useBreakpoint();
  const [openPicker, setOpenPicker] = useState(false);
  const startTime = dayjs(openTime, "h:mm A").isValid()
    ? dayjs(openTime, "h:mm A")
    : "";
  const endTime = dayjs(closeTime, "h:mm A").isValid()
    ? dayjs(closeTime, "h:mm A")
    : "";

  const handleOnClick = () => {
    if (screens.xl || screens.xxl) {
      setOpenPicker(true);
    } else {
      setOpenTimePickerModal({
        open: true,
        schedIndex,
        startTime,
        endTime,
      });
    }
  };

  return (
    <Row style={{ marginBottom: "30px" }}>
      <Col span={24} style={{ height: "40px" }}>
        <Row align="middle" justify="space-between" style={{ height: "100%" }}>
          <Col>
            <Title
              level={componentSize === "default" ? 5 : 4}
              style={{ margin: 0 }}
            >
              Schedule {+schedIndex + 1}
            </Title>
          </Col>
          <Col>
            {showDeleteIcon ? (
              <button
                className={style.removeIcon}
                onClick={() => onRemove(schedIndex)}
              >
                <RemoveIcon />
              </button>
            ) : null}
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Row gutter={{ sm: 10, xl: 15 }} justify="space-between">
          <Col xs={24} md={14} className={style.ButtonContainer}>
            <Flex justify="space-between">
              {["S", "M", "T", "W", "TH", "F", "S"].map((day, index) => (
                <Button
                  key={day + index}
                  type={days.includes(index + 1) ? "primary" : "default"}
                  className={style.buttons}
                  onClick={onDayClick.bind(this, schedIndex, index + 1)}
                  danger={error?.invalidDays}
                >
                  {day}
                </Button>
              ))}
            </Flex>
          </Col>
          <Col xs={24} md={10} className={style.DatePickerContainer}>
            <TimePicker.RangePicker
              format={"h:mm A"}
              value={[startTime, endTime]}
              onChange={(_, timeString) => onTimeChange(schedIndex, timeString)}
              status={error?.invalidTime ? "error" : ""}
              className={style.datePicker}
              inputReadOnly={true}
              open={openPicker}
              onClick={(e) => {
                e.preventDefault();
                handleOnClick();
              }}
              onOpenChange={(val) =>
                setOpenPicker(val && (screens.xl || screens.xxl))
              }
              suffixIcon={null}
            />
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        {(error?.message || error?.invalidDays || error?.invalidTime) && (
          <Text type="danger" style={{ fontSize: "12px" }}>
            {error?.message ||
              "Unable to save. Please set the day/time for this schedule."}
          </Text>
        )}
      </Col>
    </Row>
  );
};

const RemoveIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.5 3.6H18V5.4H16.2V17.1C16.2 17.5971 15.7971 18 15.3 18H2.7C2.20295 18 1.8 17.5971 1.8 17.1V5.4H0V3.6H4.5V0.9C4.5 0.402948 4.90295 0 5.4 0H12.6C13.0971 0 13.5 0.402948 13.5 0.9V3.6ZM14.4 5.4H3.6V16.2H14.4V5.4ZM10.2728 10.7997L11.8638 12.3907L10.591 13.6635L9 12.0725L7.40898 13.6635L6.13622 12.3907L7.72722 10.7997L6.13622 9.2088L7.40898 7.93602L9 9.52695L10.591 7.93602L11.8638 9.2088L10.2728 10.7997ZM6.3 1.8V3.6H11.7V1.8H6.3Z"
      fill="#FF3B30"
    />
  </svg>
);
